import { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import {
  CalendarDaysIcon, ChartBarIcon, BellIcon, ArrowPathIcon,
  CheckCircleIcon, ExclamationTriangleIcon, ClockIcon, XCircleIcon,
  ArrowTrendingUpIcon, LightBulbIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { RootState } from '../../store/store'
import { subscriptionsApi, paymentsApi, couponsApi, kycApi } from '../../services/api'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { SubscriptionPlan } from '../../types'
import { evaluatePurchaseAccess } from '../../utils/accessFlow'
import { getSelectedCheckoutPlanRef } from '../../utils/checkoutPlan'
import { BillingToggle, BillingPeriod, BILLING } from '../../components/subscription/SharedPlanCard'

const BILLING_ORDER: BillingPeriod[] = ['monthly', 'quarterly', 'yearly']

declare global { interface Window { Razorpay: any } }

function loadRazorpayScript(): Promise<boolean> {
  return new Promise(resolve => {
    if (window.Razorpay) {
      resolve(true)
      return
    }
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

// ─── Subscription card ────────────────────────────────────────────────────────

interface SubSummary {
  id: number
  plan_name: string
  status: string
  start_date: string
  end_date: string
  days_remaining: number
  amount_paid: number
}

function statusConfig(days: number, status: string) {
  if (status !== 'active') return { color: 'text-red-400', bg: 'bg-red-900/20 border-red-500/30', icon: <XCircleIcon className="w-5 h-5" />, label: 'Expired' }
  if (days <= 3) return { color: 'text-red-400', bg: 'bg-red-900/20 border-red-500/30', icon: <ExclamationTriangleIcon className="w-5 h-5" />, label: `${days}d left` }
  if (days <= 14) return { color: 'text-amber-400', bg: 'bg-amber-900/20 border-amber-500/30', icon: <ClockIcon className="w-5 h-5" />, label: `${days}d left` }
  return { color: 'text-green-400', bg: 'bg-green-900/10 border-green-500/20', icon: <CheckCircleIcon className="w-5 h-5" />, label: `${days}d left` }
}

function RenewalModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [selected, setSelected] = useState<SubscriptionPlan | null>(null)
  const [coupon, setCoupon] = useState('')
  const [couponApplying, setCouponApplying] = useState(false)
  const [couponError, setCouponError] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number } | null>(null)
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { subscriptionsApi.getPlans(true).then(setPlans).catch(() => {}) }, [])

  const [billing, setBilling] = useState<BillingPeriod>('monthly')

  const availablePeriods = useMemo(
    () => BILLING_ORDER.filter(period => plans.some(plan => (plan.billing_period || 'monthly') === period)),
    [plans]
  )

  useEffect(() => {
    if (availablePeriods.length > 0 && !availablePeriods.includes(billing)) {
      setBilling(availablePeriods[0])
    }
  }, [availablePeriods, billing])

  const visiblePlans = useMemo(() => {
    return plans.filter(plan => (plan.billing_period || 'monthly') === billing)
  }, [plans, billing])

  const baseAmount = selected ? Number(selected.final_price ?? selected.price_min) : 0
  const discount = appliedCoupon?.discount ?? 0
  const finalAmount = Math.max(0, baseAmount - discount)

  const handleApplyCoupon = async () => {
    if (!coupon.trim() || !selected) return
    setCouponApplying(true)
    setCouponError('')
    try {
      const res = await couponsApi.validate(coupon.trim().toUpperCase(), selected.id, baseAmount)
      setAppliedCoupon({ code: coupon.trim().toUpperCase(), discount: res.discount_amount })
    } catch (e: any) {
      setCouponError(e.response?.data?.detail || 'Invalid coupon')
      setAppliedCoupon(null)
    } finally { setCouponApplying(false) }
  }

  const handlePay = async () => {
    if (!selected) return
    setPaying(true)
    setError('')
    try {
      const loaded = await loadRazorpayScript()
      if (!loaded) {
        setError('Failed to load payment gateway')
        setPaying(false)
        return
      }

      const basePaise = Math.round(baseAmount * 100)
      const finalPaise = Math.round(finalAmount * 100)
      const orderRes = await paymentsApi.createOrder(selected.id, basePaise, appliedCoupon?.code)
      
      const options = {
        key: orderRes.razorpay_key,
        amount: orderRes.amount, currency: orderRes.currency,
        name: 'AnalyzerView', description: selected.name,
        order_id: orderRes.order_id,
        handler: async (response: any) => {
          try {
            await paymentsApi.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selected.id, amount: finalPaise,
              coupon_code: appliedCoupon?.code,
              discount_amount: appliedCoupon?.discount,
            })
            onSuccess()
          } catch { setError('Payment verification failed. Contact support.') }
        },
        modal: { ondismiss: () => setPaying(false) },
        theme: { color: '#6366f1' },
      }
      
      const rzp = new window.Razorpay(options)
      rzp.on('payment.failed', (resp: any) => {
        paymentsApi.markFailed(orderRes.order_id, resp?.error?.description).catch(() => {})
        setError('Payment failed. No charge was made.')
        setPaying(false)
      })
      rzp.open()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Payment failed.')
      setPaying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="bg-dark-800 border border-dark-700 rounded-2xl w-full max-w-md p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">Renew Subscription</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl leading-none">×</button>
        </div>

        {error && <div className="mb-3 bg-red-900/20 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm">{error}</div>}

        {availablePeriods.length > 1 && (
          <div className="flex justify-center mb-4">
            <BillingToggle value={billing} periods={availablePeriods} onChange={setBilling} />
          </div>
        )}

        <div className="space-y-2 mb-4">
          {visiblePlans.map(plan => (
            <button key={plan.id} onClick={() => { setSelected(plan); setAppliedCoupon(null); setCoupon('') }}
              className={`w-full text-left p-3 rounded-xl border-2 transition-all ${selected?.id === plan.id ? 'border-primary-500 bg-primary-500/5' : 'border-dark-600 bg-dark-700 hover:border-dark-500'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-semibold text-white">{plan.name}</p>
                  <p className="text-xs text-gray-400">
                    {BILLING[(plan.billing_period as BillingPeriod) || 'monthly']?.label || 'Plan'}
                  </p>
                </div>
                <div>
                  <p className="font-bold text-amber-400">₹{Number(plan.final_price ?? plan.price_min).toLocaleString('en-IN')}</p>
                  {Number(plan.savings) > 0 && (
                    <p className="text-xs text-gray-500 line-through text-right">₹{Number(plan.price_min).toLocaleString('en-IN')}</p>
                  )}
                </div>
              </div>
            </button>
          ))}
          {visiblePlans.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-4">No plans available for this billing period.</p>
          )}
        </div>

        {selected && (
          <div className="space-y-2 mb-4">
            <div className="flex gap-2">
              <input type="text" value={coupon}
                onChange={e => { setCoupon(e.target.value.toUpperCase()); setCouponError(''); setAppliedCoupon(null) }}
                placeholder="Coupon code"
                className="flex-1 bg-dark-700 border border-dark-600 text-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary-500" />
              <button onClick={handleApplyCoupon} disabled={couponApplying || !coupon.trim()}
                className="px-3 py-2 bg-dark-600 text-gray-300 rounded-lg text-sm hover:bg-dark-500 disabled:opacity-50 transition-colors">
                {couponApplying ? '…' : 'Apply'}
              </button>
            </div>
            {couponError && <p className="text-xs text-red-400">{couponError}</p>}
            {appliedCoupon && <p className="text-xs text-green-400">−₹{appliedCoupon.discount.toFixed(0)} off applied</p>}
            <div className="bg-dark-700 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between text-gray-400"><span>Subtotal</span><span>₹{baseAmount.toLocaleString('en-IN')}</span></div>
              {appliedCoupon && <div className="flex justify-between text-green-400"><span>Discount</span><span>−₹{appliedCoupon.discount.toFixed(0)}</span></div>}
              <div className="flex justify-between font-bold text-white border-t border-dark-600 pt-1"><span>Total</span><span>₹{finalAmount.toLocaleString('en-IN')}</span></div>
            </div>
          </div>
        )}

        <button onClick={handlePay} disabled={!selected || paying}
          className="w-full py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
          {paying ? 'Opening payment…' : `Pay ₹${finalAmount > 0 ? finalAmount.toLocaleString('en-IN') : '--'}`}
        </button>
      </div>
    </div>
  )
}

// ─── Main Dashboard ────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { user } = useSelector((state: RootState) => state.auth)
  const navigate = useNavigate()
  const [sub, setSub] = useState<SubSummary | null>(null)
  const [subLoading, setSubLoading] = useState(true)
  const [showRenew, setShowRenew] = useState(false)
  const [renewSuccess, setRenewSuccess] = useState(false)
  
  const [kycStatus, setKycStatus] = useState<any>(null)
  const [kycLoading, setKycLoading] = useState(true)

  const fetchSub = () => {
    setSubLoading(true)
    subscriptionsApi.getCurrentSubscription()
      .then(s => setSub(s as any))
      .catch(() => setSub(null))
      .finally(() => setSubLoading(false))
  }

  const fetchKyc = () => {
    setKycLoading(true)
    kycApi.getKYCStatus()
      .then(k => setKycStatus(k))
      .catch(() => setKycStatus(null))
      .finally(() => setKycLoading(false))
  }

  useEffect(() => { 
    fetchSub() 
    fetchKyc()
  }, [])

  const handleRenewSuccess = () => {
    setShowRenew(false)
    setRenewSuccess(true)
    fetchSub()
    setTimeout(() => setRenewSuccess(false), 6000)
  }

  const handleOpenPurchase = async () => {
    const access = await evaluatePurchaseAccess(user || null, { next: '/dashboard/subscription' })
    if (access.allowed) {
      setShowRenew(true)
      return
    }
    if (access.redirectTo) navigate(access.redirectTo)
  }

  const getKycDisplayStatus = () => {
    if (user?.kyc_status === 'approved') return 'approved'
    if (user?.kyc_status === 'rejected') return 'rejected'
    
    if (!kycStatus || kycStatus.status === 'not_started') return 'not_started'
    
    if (kycStatus.status === 'approved' || kycStatus.cams_kyc_verified === 'yes') return 'approved'
    if (kycStatus.status === 'rejected') return 'rejected'
    
    return 'under_review'
  }
  const kycDisplayStatus = getKycDisplayStatus()

  const status = sub ? statusConfig(sub.days_remaining, sub.status) : null
  const showRenewalBanner = sub && (sub.status !== 'active' || sub.days_remaining <= 14)

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

  return (
    <div className="space-y-6 max-w-4xl mx-auto py-6 px-4 sm:px-6">

      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-white">
          Welcome{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''} 👋
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          SEBI-registered advisory · INH000023719
        </p>
      </div>

      {/* Renewal success banner */}
      {renewSuccess && (
        <div className="flex items-center gap-3 bg-green-900/20 border border-green-500/30 text-green-400 px-4 py-3 rounded-xl text-sm">
          <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
          Payment successful! Your subscription has been renewed.
        </div>
      )}

      {/* Compliance & Onboarding */}
      {!kycLoading && (
        <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5 text-primary-400" />
              Compliance Status
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* KYC Status */}
            <div className={`p-4 rounded-xl border ${kycDisplayStatus === 'approved' ? 'bg-green-900/10 border-green-500/20' : kycDisplayStatus === 'under_review' ? 'bg-yellow-900/10 border-yellow-500/20' : 'bg-red-900/10 border-red-500/20'}`}>
              <div className="flex justify-between items-start mb-2">
                <span className="text-sm text-gray-400">KYC Verification</span>
                {kycDisplayStatus === 'approved' ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-400" />
                ) : kycDisplayStatus === 'under_review' ? (
                  <ClockIcon className="w-5 h-5 text-yellow-400" />
                ) : (
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                )}
              </div>
              <p className={`font-semibold ${kycDisplayStatus === 'approved' ? 'text-green-400' : kycDisplayStatus === 'under_review' ? 'text-yellow-400' : 'text-red-400'}`}>
                {kycDisplayStatus === 'approved' ? 'Approved' : kycDisplayStatus === 'under_review' ? 'Under Review' : kycDisplayStatus === 'rejected' ? 'Rejected' : 'Not Started'}
              </p>
              {kycDisplayStatus !== 'approved' && kycDisplayStatus !== 'under_review' && (
                <Link to="/dashboard/kyc" className="mt-3 inline-block px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-xs font-medium transition-colors border border-red-500/30">
                  Complete KYC →
                </Link>
              )}
            </div>

            {/* eSign Status */}
            <div className={`p-4 rounded-xl border ${kycStatus?.esign_status === 'completed' ? 'bg-green-900/10 border-green-500/20' : 'bg-red-900/10 border-red-500/20'}`}>
              <div className="flex justify-between items-start mb-2">
                <span className="text-sm text-gray-400">Client Agreement</span>
                {kycStatus?.esign_status === 'completed' ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-400" />
                ) : (
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                )}
              </div>
              <p className={`font-semibold ${kycStatus?.esign_status === 'completed' ? 'text-green-400' : 'text-red-400'}`}>
                {kycStatus?.esign_status === 'completed' ? 'Signed' : kycStatus?.esign_status === 'initiated' ? 'Pending Signature' : 'Not Started'}
              </p>
              {kycDisplayStatus !== 'not_started' && kycStatus?.esign_status !== 'completed' && (
                <Link to="/dashboard/esign" className="mt-3 inline-block px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-xs font-medium transition-colors border border-red-500/30">
                  Sign Agreement →
                </Link>
              )}
              {kycDisplayStatus === 'not_started' && kycStatus?.esign_status !== 'completed' && (
                <p className="mt-3 text-xs text-red-400/60">Complete KYC first</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Subscription card */}
      <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <CalendarDaysIcon className="w-5 h-5 text-primary-400" />
            Subscription
          </h2>
        </div>

        {subLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
        ) : sub ? (
          <div className="space-y-4">
            {/* Status row */}
            <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${status!.bg}`}>
              <span className={status!.color}>{status!.icon}</span>
              <div className="flex-1">
                <p className="font-semibold text-white">{sub.plan_name}</p>
                <p className={`text-xs ${status!.color}`}>{status!.label} · Expires {fmt(sub.end_date)}</p>
              </div>
              <button onClick={handleOpenPurchase}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-xs font-medium transition-colors">
                <ArrowPathIcon className="w-3.5 h-3.5" />
                {sub.status === 'active' ? 'Extend' : 'Renew'}
              </button>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="bg-dark-700 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-0.5">Started</p>
                <p className="font-medium text-gray-200">{fmt(sub.start_date)}</p>
              </div>
              <div className="bg-dark-700 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-0.5">Expires</p>
                <p className={`font-medium ${status!.color}`}>{fmt(sub.end_date)}</p>
              </div>
              <div className="bg-dark-700 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-0.5">Days left</p>
                <p className={`font-bold ${status!.color}`}>{Math.max(0, sub.days_remaining)}</p>
              </div>
              <div className="bg-dark-700 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-0.5">Amount paid</p>
                <p className="font-medium text-gray-200">₹{Number(sub.amount_paid).toLocaleString('en-IN')}</p>
              </div>
            </div>

            {/* Renewal warning */}
            {showRenewalBanner && (
              <div className="flex items-start gap-3 bg-amber-900/15 border border-amber-500/30 rounded-xl p-4">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-300">
                    {sub.status !== 'active' ? 'Your subscription has expired' : `Expiring in ${sub.days_remaining} day${sub.days_remaining !== 1 ? 's' : ''}`}
                  </p>
                  <p className="text-xs text-amber-400/70 mt-0.5">
                    Renew now to continue receiving research recommendations without interruption.
                  </p>
                </div>
                <button onClick={handleOpenPurchase}
                  className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold transition-colors flex-shrink-0">
                  Renew
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 space-y-3">
            <XCircleIcon className="w-10 h-10 text-gray-600 mx-auto" />
            <p className="text-gray-400">No active subscription</p>
            <button onClick={() => {
                const planRef = getSelectedCheckoutPlanRef();
                navigate(planRef ? `/dashboard/subscription?planSlug=${planRef}` : '/dashboard/subscription', { state: { selectedPlan: planRef } });
              }}
              className="px-5 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-xl text-sm font-medium transition-colors">
              Choose a Plan
            </button>
          </div>
        )}
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link to="/dashboard/recommendations"
          className="flex items-center gap-3 bg-dark-800 border border-dark-700 rounded-xl p-4 hover:border-dark-600 transition-colors">
          <div className="w-9 h-9 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
            <LightBulbIcon className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Recommendations</p>
            <p className="text-xs text-gray-400">View all research calls</p>
          </div>
        </Link>
        <Link to="/dashboard/notifications"
          className="flex items-center gap-3 bg-dark-800 border border-dark-700 rounded-xl p-4 hover:border-dark-600 transition-colors">
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <BellIcon className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Alerts</p>
            <p className="text-xs text-gray-400">Price & plan alerts</p>
          </div>
        </Link>
        <Link to="/dashboard/profile"
          className="flex items-center gap-3 bg-dark-800 border border-dark-700 rounded-xl p-4 hover:border-dark-600 transition-colors">
          <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
            <ChartBarIcon className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Profile</p>
            <p className="text-xs text-gray-400">KYC & account details</p>
          </div>
        </Link>
      </div>

      {/* Info boxes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <ArrowTrendingUpIcon className="w-4 h-4 text-green-400" />
            About Your Advisory
          </h3>
          <ul className="space-y-2 text-xs text-gray-400">
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />SEBI registered research analyst (INH000023719)</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />Fixed-fee advisory — no commissions or profit sharing</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />No execution access — you retain full control</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />BSE RAASB Enlistment No. 7041</li>
          </ul>
        </div>
        <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-white mb-3">Need Help?</h3>
          <p className="text-xs text-gray-400 mb-3">Our team is available to help with plan queries, technical issues, or compliance questions.</p>
          <Link to="/contact"
            className="inline-flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 font-medium transition-colors">
            Contact Support →
          </Link>
        </div>
      </div>

      {/* Renewal modal */}
      {showRenew && <RenewalModal onClose={() => setShowRenew(false)} onSuccess={handleRenewSuccess} />}
    </div>
  )
}
